import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  TrophyIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  EyeSlashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  ChevronDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CurrencyDollarIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import * as Flags from 'country-flag-icons/react/3x2';
import { hasFlag } from 'country-flag-icons';
import { supabase, fetchHoleScoresPaginated } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import PrizeBreakdownModal from '../components/PrizeBreakdownModal';
import { computeLeaderboard, sortLeaderboard, formatScore as formatScoreUtil, formatRoundScore, scoreColor, calcRoundScore, type ScoringMethod } from '../lib/scoring';

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
const ALL_FLAG_CODES = Object.keys(Flags) as (keyof typeof Flags)[];

function resolveCountryCode(country: string): string | null {
  const trimmed = country.trim();
  const upper = trimmed.toUpperCase();
  if (upper.length === 2 && hasFlag(upper)) return upper;
  for (const code of ALL_FLAG_CODES) {
    try {
      if (regionNames.of(code)?.toLowerCase() === trimmed.toLowerCase()) return code;
    } catch { /* ignore */ }
  }
  return null;
}

function CountryFlag({ country }: { country: string }) {
  const code = resolveCountryCode(country);
  if (!code) return null;
  const FlagComponent = Flags[code as keyof typeof Flags];
  return <FlagComponent title={country.trim()} className="w-4 h-3 rounded-sm flex-shrink-0" />;
}

interface PlayerAward {
  id: string;
  player_id: string;
  award_id: string;
  award_name: string;
  player_name: string;
  notes?: string;
}

interface CategoryAward {
  category: string;
  player_name: string;
  player_id: string;
  award_name: string;
  award_id: string;
  notes?: string;
}

interface CategoryWinner {
  category: string;
  player_name: string;
  total_score: number;
}

interface LeaderboardEntry {
  id: string;
  tournament_id: string;
  player_name: string;
  country: string;
  resident_club: string;
  category: string;
  handicap: number;
  total_score: number;
  round_scores: number[];
  rounds_played: number;
  position: number;
  overall_status?: 'PLAYED' | 'NO_SCORE' | 'DNP' | 'WD' | 'DQ';
  thru: number;
}

interface Tournament {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  scoring_method?: string;
}

const formatScore = (score: number): string => {
  if (score === 0) return 'E';
  if (score >= 80) return '--';
  return score > 0 ? `+${score}` : score.toString();
};

export default function Leaderboard() {
  const [searchParams] = useSearchParams();
  const tournamentQueryParam = searchParams.get('tournament');
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [selectedRound, setSelectedRound] = useState(0);
  const [totalRounds, setTotalRounds] = useState(0);
  const [recordsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [loadingAwards, setLoadingAwards] = useState(true);
  const [playerAwards, setPlayerAwards] = useState<PlayerAward[]>([]);
  const [categoryAwards, setCategoryAwards] = useState<CategoryAward[]>([]);
  const [sortField, setSortField] = useState<string>('position');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [awardSortCache, setAwardSortCache] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [filteredLeaderboard, setFilteredLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [isPublished, setIsPublished] = useState(true);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState<string>('');
  const [showPrizeBreakdown, setShowPrizeBreakdown] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<LeaderboardEntry | null>(null);
  const [playerHoleScores, setPlayerHoleScores] = useState<{ round: number; hole: number; strokes: number }[]>([]);
  const [playerPairings, setPlayerPairings] = useState<{ round_number: number; group_number: number; tee_time: string | null; starting_hole: number | null; date: string | null; partners: { name: string; handicap: number | null }[] }[]>([]);
  const [loadingPlayerDetails, setLoadingPlayerDetails] = useState(false);
  const [roundNames, setRoundNames] = useState<Record<number, string>>({});
  const [roundIds, setRoundIds] = useState<Record<number, string>>({});
  const [roundPairedPlayerIds, setRoundPairedPlayerIds] = useState<Set<string>>(new Set());
  const [scoringMethod, setScoringMethod] = useState<ScoringMethod>('stroke_play_net');
  const [courseHoleParMap, setCourseHoleParMap] = useState<Record<number, number>>({});
  const [courseHoleHcpMap, setCourseHoleHcpMap] = useState<Record<number, number>>({});
  const [enabledRounds, setEnabledRounds] = useState<Set<number> | undefined>(undefined);
  const itemsPerPage = 20;

  useEffect(() => {
    fetchTournamentsWithPreselect(tournamentQueryParam);
  }, [tournamentQueryParam]);

  useEffect(() => {
    if (selectedTournamentId) {
      fetchLeaderboard();
      checkLeaderboardPublishStatus();
      fetchPlayerAwards();
      fetchCategoryAwards();
    }
  }, [selectedTournamentId, scoringMethod]);

  useEffect(() => {
    if (selectedRound > 0 && roundIds[selectedRound] && selectedTournamentId) {
      supabase
        .from('player_pairings')
        .select('player_id')
        .eq('round_id', roundIds[selectedRound])
        .eq('tournament_id', selectedTournamentId)
        .then(({ data }) => {
          setRoundPairedPlayerIds(new Set((data || []).map(p => p.player_id)));
        });
    } else {
      setRoundPairedPlayerIds(new Set());
    }
  }, [selectedRound, roundIds, selectedTournamentId]);

  useEffect(() => {
    filterLeaderboard();
  }, [leaderboard, searchTerm, selectedCategory, selectedRound, roundPairedPlayerIds, scoringMethod]);

  useEffect(() => {
    if (selectedPlayer && selectedTournamentId) {
      fetchPlayerDetails(selectedPlayer.id);
    } else {
      setPlayerHoleScores([]);
      setPlayerPairings([]);
    }
  }, [selectedPlayer?.id]);

  const fetchPlayerDetails = async (playerId: string) => {
    setLoadingPlayerDetails(true);
    try {
      const [holeRes, roundsRes, playerPairingsRes] = await Promise.all([
        supabase
          .from('hole_scores')
          .select('round, hole, strokes')
          .eq('player_id', playerId)
          .eq('tournament_id', selectedTournamentId)
          .order('round')
          .order('hole'),
        supabase
          .from('tournament_rounds')
          .select('id, round_number, name, date')
          .eq('tournament_id', selectedTournamentId)
          .order('sort_order', { ascending: true }),
        supabase
          .from('player_pairings')
          .select('round_id')
          .eq('player_id', playerId)
          .eq('tournament_id', selectedTournamentId),
      ]);

      const pairedRoundIds = new Set((playerPairingsRes.data || []).map(p => p.round_id));
      const allPairedRounds = (roundsRes.data || []).filter(r => pairedRoundIds.has(r.id));
      const holeScores = holeRes.data || [];
      const roundsWithScores = new Set(holeScores.map(h => h.round));

      const augmentedHoleScores = [...holeScores];
      allPairedRounds.forEach(r => {
        if (!roundsWithScores.has(r.round_number)) {
          augmentedHoleScores.push({ round: r.round_number, hole: 0, strokes: 0 });
        }
      });

      setPlayerHoleScores(augmentedHoleScores);

      if (roundsRes.data && roundsRes.data.length > 0) {
        const fetchedRoundIds = roundsRes.data.map(r => r.id);
        const roundNumMap: Record<string, number> = {};
        const roundDateMap: Record<string, string | null> = {};
        roundsRes.data.forEach(r => {
          roundNumMap[r.id] = r.round_number;
          roundDateMap[r.id] = r.date || null;
        });

        const { data: pairingRows } = await supabase
          .from('player_pairings')
          .select('*')
          .in('round_id', fetchedRoundIds);

        if (pairingRows) {
          const playerGroups: Record<string, { round_id: string; group_number: number; tee_time: string | null; starting_hole: number | null }> = {};
          pairingRows.forEach(p => {
            if (p.player_id === playerId) {
              playerGroups[p.round_id] = { round_id: p.round_id, group_number: p.group_number, tee_time: p.tee_time, starting_hole: p.starting_hole ?? null };
            }
          });

          const partnerIds = new Set<string>();
          Object.entries(playerGroups).forEach(([roundId, { group_number }]) => {
            pairingRows
              .filter(p => p.round_id === roundId && p.group_number === group_number && p.player_id !== playerId)
              .forEach(p => partnerIds.add(p.player_id));
          });

          let playerInfoMap: Record<string, { name: string; handicap: number | null }> = {};
          if (partnerIds.size > 0) {
            const { data: playerRows } = await supabase
              .from('players')
              .select('id, first_name, last_name, handicap')
              .in('id', Array.from(partnerIds));
            if (playerRows) {
              playerRows.forEach(p => {
                playerInfoMap[p.id] = {
                  name: `${p.first_name} ${p.last_name}`.trim(),
                  handicap: p.handicap ?? null,
                };
              });
            }
          }

          const pairingsResult = Object.entries(playerGroups).map(([roundId, { group_number, tee_time, starting_hole }]) => {
            const partners = pairingRows
              .filter(p => p.round_id === roundId && p.group_number === group_number && p.player_id !== playerId)
              .map(p => playerInfoMap[p.player_id] || { name: 'Unknown', handicap: null });
            return {
              round_number: roundNumMap[roundId] ?? 0,
              group_number,
              tee_time,
              starting_hole,
              date: roundDateMap[roundId] || null,
              partners,
            };
          }).sort((a, b) => a.round_number - b.round_number);

          setPlayerPairings(pairingsResult);
        }
      }
    } catch (err) {
      console.error('Error fetching player details:', err);
    } finally {
      setLoadingPlayerDetails(false);
    }
  };

  const checkLeaderboardPublishStatus = async () => {
    try {
      // Check if leaderboard data is available and published
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'leaderboard_published')
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking leaderboard publish status:', error);
        return;
      }

      // If no setting exists, assume leaderboard is published
      const isPublished = data?.value?.published !== false;
      
      if (!isPublished) {
        setError('Leaderboard is not currently published');
        return;
      }
      
      setIsPublished(isPublished);
    } catch (err) {
      console.error('Error checking leaderboard publish status:', err);
    }
  };

  const filterLeaderboard = () => {
    let filtered = [...leaderboard];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        entry =>
          entry.player_name.toLowerCase().includes(term) ||
          entry.country.toLowerCase().includes(term) ||
          entry.resident_club.toLowerCase().includes(term)
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(entry => entry.category === selectedCategory);
      // Recalculate positions within filtered category
      if (selectedRound === 0) {
        let pos = 1;
        for (let i = 0; i < filtered.length; i++) {
          if (filtered[i].overall_status !== 'PLAYED') {
            filtered[i] = { ...filtered[i], position: 0 };
            continue;
          }
          if (i > 0 && filtered[i].total_score === filtered[i - 1].total_score && filtered[i - 1].overall_status === 'PLAYED') {
            filtered[i] = { ...filtered[i], position: filtered[i - 1].position };
          } else {
            filtered[i] = { ...filtered[i], position: pos };
          }
          pos = i + 2;
        }
      }
    }

    if (selectedRound > 0) {
      filtered = filtered
        .filter(entry => {
          const roundScore = entry.round_scores?.[selectedRound - 1];
          const hasScore = roundScore != null && roundScore !== 0;
          const isPaired = roundPairedPlayerIds.has(entry.id);
          return hasScore || isPaired;
        })
        .sort((a, b) => {
          const aScore = a.round_scores?.[selectedRound - 1];
          const bScore = b.round_scores?.[selectedRound - 1];
          const aHasScore = aScore != null && aScore !== 0;
          const bHasScore = bScore != null && bScore !== 0;
          if (aHasScore && !bHasScore) return -1;
          if (!aHasScore && bHasScore) return 1;
          if (!aHasScore && !bHasScore) return a.player_name.localeCompare(b.player_name);
          return scoringMethod === 'stableford_net' ? bScore! - aScore! : aScore! - bScore!;
        });

      // Assign positions with tie handling
      let pos = 1;
      for (let i = 0; i < filtered.length; i++) {
        const score = filtered[i].round_scores?.[selectedRound - 1];
        const hasScore = score != null && score !== 0;
        if (!hasScore) {
          filtered[i] = { ...filtered[i], position: 0 };
          continue;
        }
        const prevScore = i > 0 ? filtered[i - 1].round_scores?.[selectedRound - 1] : null;
        const prevHasScore = prevScore != null && prevScore !== 0;
        if (i > 0 && prevHasScore && score === prevScore) {
          filtered[i] = { ...filtered[i], position: filtered[i - 1].position };
        } else {
          filtered[i] = { ...filtered[i], position: pos };
        }
        pos = i + 2;
      }
    }

    setFilteredLeaderboard(filtered);
    setCurrentPage(1);
  };

  const fetchTournamentsWithPreselect = async (preselectedId: string | null) => {
    try {
      const { data, error } = await supabase
        .from('tournaments')
        .select('id, name, start_date, end_date, status')
        .in('status', ['ongoing', 'completed', 'upcoming'])
        .order('start_date', { ascending: false });

      if (error) throw error;

      setTournaments(data || []);

      if (data && data.length > 0) {
        const preselect = preselectedId && data.find(t => t.id === preselectedId);
        const target = preselect || data[0];
        const targetId = target.id;
        setSelectedTournamentId(targetId);
        const methodData = await supabase
          .from('system_settings')
          .select('value')
          .eq('key', `scoring_method_${targetId}`)
          .maybeSingle()
          .then(r => r.data)
          .catch(() => null);
        setScoringMethod((methodData?.value as ScoringMethod) || 'stroke_play_net');
        fetchLeaderboardForTournament(targetId);
        fetchPlayerAwardsForTournament(targetId);
        fetchCategoryAwardsForTournament(targetId);
        checkLeaderboardPublishStatus();
      }
    } catch (err) {
      console.error('Error fetching tournaments:', err);
      toast.error('Failed to load tournaments');
    }
  };

  const fetchLeaderboardForTournament = async (tournamentId: string) => {
    if (!tournamentId) return;

    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('leaderboard_view')
        .select('*')
        .eq('tournament_id', tournamentId);

      if (selectedCategory !== 'all') {
        query = query.eq('category', selectedCategory);
      }

      const { data, error } = await query;

      if (error) throw error;

      const playerIds = (data || []).map(e => e.id);
      let holeScoreMap: Record<string, { round: number; hole: number; strokes: number; score_status?: string }[]> = {};

      // Fetch hole scores and course hole data in parallel
      const { data: tournamentCourse } = await supabase
        .from('tournament_courses')
        .select('course_id')
        .eq('tournament_id', tournamentId)
        .maybeSingle();

      let holeParMap: Record<number, number> = {};
      let holeHcpMap: Record<number, number> = {};
      if (tournamentCourse?.course_id) {
        const { data: holesData } = await supabase
          .from('holes')
          .select('hole_number, par, handicap_index')
          .eq('course_id', tournamentCourse.course_id)
          .order('hole_number');
        if (holesData) {
          for (const h of holesData) {
            holeParMap[h.hole_number] = h.par;
            holeHcpMap[h.hole_number] = h.handicap_index ?? h.hole_number;
          }
        }
      }
      setCourseHoleParMap(holeParMap);
      setCourseHoleHcpMap(holeHcpMap);

      if (playerIds.length > 0) {
        const holeRows = await fetchHoleScoresPaginated(tournamentId, playerIds);
        for (const row of holeRows) {
          if (!holeScoreMap[row.player_id]) holeScoreMap[row.player_id] = [];
          holeScoreMap[row.player_id].push({ round: row.round, hole: row.hole, strokes: row.strokes, score_status: row.score_status });
        }
      }

      // Fetch rounds and disabled rounds setting
      const [{ data: roundsData }, { data: disabledSetting }] = await Promise.all([
        supabase
          .from('tournament_rounds')
          .select('*')
          .eq('tournament_id', tournamentId)
          .order('sort_order', { ascending: true }),
        supabase
          .from('system_settings')
          .select('value')
          .eq('key', `disabled_rounds_${tournamentId}`)
          .maybeSingle(),
      ]);

      const disabledArr: number[] = Array.isArray(disabledSetting?.value) ? disabledSetting.value : [];
      const disabledSet = new Set(disabledArr);

      let localEnabledRounds: Set<number> | undefined = undefined;
      if (roundsData && roundsData.length > 0) {
        const names: Record<number, string> = {};
        const ids: Record<number, string> = {};
        const enabled = new Set<number>();
        roundsData.forEach(r => {
          names[r.round_number] = r.name || `Round ${r.round_number}`;
          ids[r.round_number] = r.id;
          if (!disabledSet.has(r.round_number)) enabled.add(r.round_number);
        });
        setRoundNames(names);
        setRoundIds(ids);
        setTotalRounds(roundsData.length);
        setEnabledRounds(enabled);
        localEnabledRounds = enabled;
      }

      const withScores = (data || []).map(entry => {
        const playerHoles = holeScoreMap[entry.id];
        if (!playerHoles || playerHoles.length === 0) return { ...entry, thru: 0 };

        const computed = computeLeaderboard(
          [{ id: entry.id, handicap: entry.handicap ?? 0 }],
          holeScoreMap,
          holeParMap,
          holeHcpMap,
          scoringMethod,
          localEnabledRounds,
        )[0];

        return {
          ...entry,
          total_score: computed.total_score,
          round_scores: computed.round_scores,
          rounds_played: computed.rounds_played,
          overall_status: entry.overall_status || 'PLAYED',
          thru: computed.thru,
        };
      });

      const activePlayers = withScores.filter(e => e.overall_status !== 'WD');

      const sorted = sortLeaderboard(activePlayers, scoringMethod);

      // Reassign positions after sorting, handling ties
      let currentPosition = 1;
      for (let i = 0; i < sorted.length; i++) {
        if (sorted[i].overall_status !== 'PLAYED') {
          sorted[i].position = 0;
          continue;
        }
        if (i > 0 && sorted[i].total_score === sorted[i - 1].total_score && sorted[i - 1].overall_status === 'PLAYED') {
          sorted[i].position = sorted[i - 1].position;
        } else {
          sorted[i].position = currentPosition;
        }
        currentPosition = i + 2;
      }

      setLeaderboard(sorted);
      setFilteredLeaderboard(sorted);

      if (!roundsData || roundsData.length === 0) {
        const maxRounds = activePlayers.reduce((max, entry) => {
          const len = entry.round_scores?.length ?? 0;
          return len > max ? len : max;
        }, 0);
        setTotalRounds(maxRounds);
      }

      const uniqueCategories = [...new Set(data?.map(entry => entry.category) || [])];
      setCategories(uniqueCategories);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setError('Failed to load leaderboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayerAwardsForTournament = async (_tournamentId: string) => {
    try {
      const { data, error } = await supabase
        .from('player_awards_view')
        .select('*')
        .order('player_name');
      if (error) throw error;
      setPlayerAwards(data || []);
    } catch (err) {
      console.error('Error fetching player awards:', err);
    }
  };

  const fetchCategoryAwardsForTournament = async (_tournamentId: string) => {
    try {
      setLoadingAwards(true);
      const { data: awardsData, error } = await supabase
        .from('player_awards')
        .select(`
          id,
          player_id,
          award_id,
          notes,
          players:player_id (first_name, last_name, category),
          award_categories:award_id (name)
        `)
        .order('player_id');
      if (error) throw error;
      if (awardsData) {
        const awards: CategoryAward[] = awardsData.map(award => ({
          category: award.players?.category || 'Unknown',
          player_name: `${award.players?.first_name || ''} ${award.players?.last_name || ''}`.trim(),
          player_id: award.player_id,
          award_name: award.award_categories?.name || 'Unknown Award',
          award_id: award.award_id,
          notes: award.notes
        }));
        setCategoryAwards(awards);
      }
    } catch (err) {
      console.error('Error fetching category awards:', err);
    } finally {
      setLoadingAwards(false);
    }
  };

  const fetchLeaderboard = async () => {
    if (!selectedTournamentId) return;
    fetchLeaderboardForTournament(selectedTournamentId);
  };

  const fetchPlayerAwards = async () => {
    try {
      const { data, error } = await supabase
        .from('player_awards_view')
        .select('*')
        .order('player_name');

      if (error) throw error;

      setPlayerAwards(data || []);
    } catch (err) {
      console.error('Error fetching player awards:', err);
    }
  };

  const fetchCategoryAwards = async () => {
    try {
      setLoadingAwards(true);
      
      // Get all awards with player and award details
      const { data: awardsData, error } = await supabase
        .from('player_awards')
        .select(`
          id,
          player_id,
          award_id,
          notes,
          players:player_id (first_name, last_name, category),
          award_categories:award_id (name)
        `)
        .order('player_id');
      
      if (error) throw error;
      
      if (awardsData) {
        // Group awards by category
        const awards: CategoryAward[] = awardsData.map(award => ({
          category: award.players?.category || 'Unknown',
          player_name: `${award.players?.first_name || ''} ${award.players?.last_name || ''}`.trim(),
          player_id: award.player_id,
          award_name: award.award_categories?.name || 'Unknown Award',
          award_id: award.award_id,
          notes: award.notes
        }));
        
        setCategoryAwards(awards);
      }
    } catch (err) {
      console.error('Error fetching category awards:', err);
    } finally {
      setLoadingAwards(false);
    }
  };

  const checkSupabaseConnection = async () => {
    try {
      const { data, error } = await supabase.from('leaderboard_view').select('count');
      return !error;
    } catch {
      return false;
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredLeaderboard.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEntries = filteredLeaderboard.slice(startIndex, endIndex);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  // Get player awards for a specific player
  const getPlayerAwardsById = (playerId: string) => {
    return playerAwards.filter(award => award.player_id === playerId);
  };

  if (loading && selectedTournamentId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-golf-orange mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg">{error}</p>
          <button
            onClick={fetchLeaderboard}
            className="mt-4 bg-golf-orange text-white px-4 py-2 rounded hover:bg-orange-600"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Unpublished Notice */}
      {!isPublished && (
        <div className="fixed top-24 inset-x-0 z-50 bg-yellow-100 border-b border-yellow-200 py-2">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-center gap-2 text-yellow-800">
              <EyeSlashIcon className="w-5 h-5" />
              <p className="text-sm font-medium">
                This page is currently unpublished and only visible to administrators
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative bg-golf-gray text-white py-16 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://wjwcuakqeesxnbjznajh.supabase.co/storage/v1/object/public/images/webcontents/0B6A9777.jpg')] bg-cover bg-center opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              Leaderboard{selectedTournamentId && tournaments.find(t => t.id === selectedTournamentId)?.start_date
                ? ` · ${new Date(tournaments.find(t => t.id === selectedTournamentId)!.start_date).getFullYear()}`
                : ''}
            </h1>
            <p className="text-xl md:text-2xl opacity-90 mb-6">
              Live scores and rankings
            </p>
{selectedTournamentId && (
              <a
                href={`/prize-breakdown?tournament=${selectedTournamentId}`}
                className="inline-flex items-center space-x-2 bg-white text-golf-orange px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              >
                <CurrencyDollarIcon className="w-5 h-5" />
                <span>View Prize Breakdown</span>
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Leaderboard Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Search and Filters */}
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by player name, country, or club..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
                    />
                  </div>
                </div>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <FunnelIcon className="w-5 h-5 text-gray-500" />
                  <span className="text-gray-700">Filters</span>
                  <ChevronDownIcon className={`w-4 h-4 text-gray-500 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </button>
              </div>
              
              {showFilters && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="tournament" className="block text-sm font-medium text-gray-700 mb-1">
                        Tournament
                      </label>
                      <select
                        id="tournament"
                        value={selectedTournamentId}
                        onChange={async (e) => {
                          setSelectedTournamentId(e.target.value);
                          const methodData = await supabase
                            .from('system_settings')
                            .select('value')
                            .eq('key', `scoring_method_${e.target.value}`)
                            .maybeSingle()
                            .then(r => r.data)
                            .catch(() => null);
                          setScoringMethod((methodData?.value as ScoringMethod) || 'stroke_play_net');
                          setCurrentPage(1);
                        }}
                        className="w-full border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
                      >
                        {tournaments.map(tournament => (
                          <option key={tournament.id} value={tournament.id}>
                            {tournament.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                        Player Category
                      </label>
                      <select
                        id="category"
                        value={selectedCategory}
                        onChange={(e) => {
                          setSelectedCategory(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="w-full border border-gray-300 rounded-lg focus:ring-golf-orange focus:border-golf-orange"
                      >
                        <option value="all">All Categories</option>
                        {categories.map(category => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Round Selector */}
            {totalRounds > 0 && (
              <div className="px-4 sm:px-6 py-3 border-b border-gray-200 bg-gray-50 flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mr-1">Round:</span>
                <button
                  onClick={() => { setSelectedRound(0); setCurrentPage(1); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    selectedRound === 0
                      ? 'bg-golf-green text-white shadow-sm'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-golf-green hover:text-golf-green'
                  }`}
                >
                  Overall
                </button>
                {Array.from({ length: totalRounds }, (_, i) => {
                  const roundColors = [
                    { active: 'bg-blue-500 text-white shadow-sm', inactive: 'text-blue-600 bg-blue-50 border border-blue-200 hover:bg-blue-100' },
                    { active: 'bg-amber-500 text-white shadow-sm', inactive: 'text-amber-600 bg-amber-50 border border-amber-200 hover:bg-amber-100' },
                    { active: 'bg-emerald-500 text-white shadow-sm', inactive: 'text-emerald-600 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100' },
                    { active: 'bg-rose-500 text-white shadow-sm', inactive: 'text-rose-600 bg-rose-50 border border-rose-200 hover:bg-rose-100' },
                    { active: 'bg-cyan-500 text-white shadow-sm', inactive: 'text-cyan-600 bg-cyan-50 border border-cyan-200 hover:bg-cyan-100' },
                  ];
                  const color = roundColors[i % roundColors.length];
                  if (enabledRounds && !enabledRounds.has(i + 1)) return null;
                  return (
                    <button
                      key={i + 1}
                      onClick={() => { setSelectedRound(i + 1); setCurrentPage(1); }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                        selectedRound === i + 1 ? color.active : color.inactive
                      }`}
                    >
                      {roundNames[i + 1] || `Round ${i + 1}`}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Leaderboard List */}
            <div>
              {tournaments.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">No tournaments available</p>
                </div>
              ) : leaderboard.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">No leaderboard data available for this tournament</p>
                </div>
              ) : (
                <div>
                  <div className="divide-y divide-gray-100">
                    {currentEntries.map((entry) => {
                      const playerAwardsList = getPlayerAwardsById(entry.id);
                      const score = selectedRound > 0
                        ? (entry.round_scores?.[selectedRound - 1] ?? null)
                        : entry.total_score;

                      return (
                        <div
                          key={entry.id}
                          onClick={() => setSelectedPlayer(entry)}
                          className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                        >
                          {/* Position */}
                          <div className="flex-shrink-0 w-10">
                            {entry.overall_status === 'PLAYED' ? (
                              <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full text-sm font-bold ${
                                entry.position === 1 ? 'bg-amber-100 text-amber-700 ring-2 ring-amber-300' :
                                entry.position === 2 ? 'bg-gray-100 text-gray-700 ring-2 ring-gray-300' :
                                entry.position === 3 ? 'bg-orange-100 text-orange-700 ring-2 ring-orange-300' :
                                'bg-gray-50 text-gray-600'
                              }`}>
                                {entry.position}
                              </span>
                            ) : (
                              <span className="inline-flex items-center justify-center w-9 h-9 text-sm font-medium text-gray-300">
                                --
                              </span>
                            )}
                          </div>

                          {/* Player Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-semibold text-gray-900 truncate">{entry.player_name}</p>
                              {playerAwardsList.length > 0 && (
                                <TrophyIcon className="w-4 h-4 text-amber-500 flex-shrink-0" />
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                              <span>{entry.resident_club}</span>
                              <span className="text-gray-300">|</span>
                              {entry.country && (
                                <span className="inline-flex items-center gap-1">
                                  <CountryFlag country={entry.country} />
                                  <span>{entry.country}</span>
                                </span>
                              )}
                              <span className="text-gray-300">|</span>
                              <span className="inline-flex px-1.5 py-0.5 rounded bg-green-50 text-green-700 font-medium text-[11px]">{entry.category}</span>
                              <span className="text-gray-300">|</span>
                              <span>HCP {entry.handicap}</span>
                            </div>
                          </div>

                          {/* Score */}
                          <div className="flex-shrink-0 text-right">
                            {entry.overall_status === 'PLAYED' ? (
                              <>
                                <p className={`text-lg font-bold leading-tight ${
                                  score == null ? 'text-gray-300' : scoreColor(score, scoringMethod)
                                }`}>
                                  {score == null ? '--' : formatScoreUtil(score, scoringMethod)}
                                </p>
                                <p className="text-[11px] text-gray-400 mt-0.5">
                                  {selectedRound > 0 ? `R${selectedRound}` : entry.thru > 0 ? (entry.thru >= 18 ? 'F' : `Thru ${entry.thru}`) : `${entry.rounds_played} rnd${entry.rounds_played !== 1 ? 's' : ''}`}
                                </p>
                              </>
                            ) : (
                              <span className={`inline-flex px-2 py-1 text-[11px] font-semibold rounded-full ${
                                entry.overall_status === 'DNP' ? 'bg-gray-100 text-gray-600' :
                                entry.overall_status === 'WD' ? 'bg-yellow-50 text-yellow-700' :
                                entry.overall_status === 'DQ' ? 'bg-red-50 text-red-700' :
                                'bg-gray-100 text-gray-500'
                              }`}>
                                {entry.overall_status === 'DNP' ? 'DNP' :
                                 entry.overall_status === 'WD' ? 'WD' :
                                 entry.overall_status === 'DQ' ? 'DQ' :
                                 'No Score'}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                          currentPage === 1
                            ? 'text-gray-300 cursor-not-allowed bg-gray-100'
                            : 'text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                          currentPage === totalPages
                            ? 'text-gray-300 cursor-not-allowed bg-gray-100'
                            : 'text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                      >
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Showing{' '}
                          <span className="font-medium">{startIndex + 1}</span>
                          {' '}to{' '}
                          <span className="font-medium">
                            {Math.min(endIndex, filteredLeaderboard.length)}
                          </span>
                          {' '}of{' '}
                          <span className="font-medium">{filteredLeaderboard.length}</span>
                          {' '}results
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                          <button
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                              currentPage === 1
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            <ChevronLeftIcon className="h-5 w-5" />
                          </button>
                          {getPageNumbers().map(number => (
                            <button
                              key={number}
                              onClick={() => paginate(number)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === number
                                  ? 'z-10 bg-golf-orange border-golf-orange text-white'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {number}
                            </button>
                          ))}
                          <button
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                              currentPage === totalPages
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            <ChevronRightIcon className="h-5 w-5" />
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Prize Breakdown Modal */}
      {selectedTournamentId && (
        <PrizeBreakdownModal
          isOpen={showPrizeBreakdown}
          onClose={() => setShowPrizeBreakdown(false)}
          tournamentId={selectedTournamentId}
          tournamentName={tournaments.find(t => t.id === selectedTournamentId)?.name || 'Tournament'}
        />
      )}

      {/* Player Details Modal */}
      {selectedPlayer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedPlayer(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden">
            <div className="bg-gradient-to-r from-golf-green to-emerald-700 px-6 py-5">
              <button
                onClick={() => setSelectedPlayer(null)}
                className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
              <h3 className="text-xl font-bold text-white">{selectedPlayer.player_name}</h3>
              <p className="text-white/70 text-sm mt-0.5">
                {selectedPlayer.resident_club}{selectedPlayer.country ? ` \u00B7 ${selectedPlayer.country}` : ''}
              </p>
            </div>

            <div className="p-6 space-y-5 overflow-y-auto max-h-[calc(85vh-88px)]">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 font-medium uppercase">Category</p>
                  <p className="text-sm font-bold text-gray-800 mt-1">{selectedPlayer.category}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 font-medium uppercase">Handicap</p>
                  <p className="text-sm font-bold text-gray-800 mt-1">{selectedPlayer.handicap}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 font-medium uppercase">Position</p>
                  <p className="text-sm font-bold text-gray-800 mt-1">
                    {selectedPlayer.overall_status === 'PLAYED' ? selectedPlayer.position : '--'}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between py-3 border-y border-gray-100">
                <div>
                  <p className="text-xs text-gray-500">Total Score</p>
                  <p className={`text-2xl font-bold ${
                    selectedPlayer.overall_status === 'PLAYED' ? scoreColor(selectedPlayer.total_score, scoringMethod) : 'text-gray-300'
                  }`}>
                    {selectedPlayer.overall_status === 'PLAYED' ? formatScoreUtil(selectedPlayer.total_score, scoringMethod) : '--'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Rounds Played</p>
                  <p className="text-2xl font-bold text-gray-800">{selectedPlayer.rounds_played}</p>
                </div>
              </div>

              {loadingPlayerDetails ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-golf-green border-t-transparent" />
                </div>
              ) : (
                <>
                  {/* Scorecard - Hole by Hole */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">
                      {scoringMethod === 'stableford_net' ? 'Stableford (Net)' : 'Stroke Play (Net)'}
                    </h4>
                    {playerHoleScores.length > 0 ? (() => {
                      const rounds = [...new Set(playerHoleScores.map(h => h.round))].sort((a, b) => a - b)
                        .filter(r => !enabledRounds || enabledRounds.has(r));
                      return (
                        <div className="space-y-3">
                          {rounds.map(roundNum => {
                            const holes = playerHoleScores.filter(h => h.round === roundNum && h.hole > 0).sort((a, b) => a.hole - b.hole);
                            const hasScores = holes.length > 0;

                            if (!hasScores) {
                              return (
                                <div key={roundNum} className="border border-gray-100 rounded-xl overflow-hidden">
                                  <div className="bg-gray-50 px-3 py-2 flex items-center justify-between">
                                    <span className="text-xs font-bold text-gray-700 uppercase">Round {roundNum}</span>
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">Pending</span>
                                  </div>
                                  <div className="px-4 py-4 text-center">
                                    <p className="text-sm text-gray-400">Scores will appear once play begins</p>
                                  </div>
                                </div>
                              );
                            }

                            const front9 = holes.filter(h => h.hole <= 9);
                            const back9 = holes.filter(h => h.hole > 9);
                            const front9Total = front9.reduce((sum, h) => sum + h.strokes, 0);
                            const back9Total = back9.reduce((sum, h) => sum + h.strokes, 0);
                            const roundTotal = front9Total + back9Total;
                            const roundNetScore = calcRoundScore(
                              holes.map(h => ({ hole: h.hole, strokes: h.strokes })),
                              selectedPlayer.handicap,
                              courseHoleParMap,
                              courseHoleHcpMap,
                              scoringMethod,
                            );
                            return (
                              <div key={roundNum} className="border border-gray-100 rounded-xl overflow-hidden">
                                <div className="bg-gray-50 px-3 py-2 flex items-center justify-between">
                                  <span className="text-xs font-bold text-gray-700 uppercase">Round {roundNum}</span>
                                  <div className="flex items-center gap-3">
                                    <span className="text-xs text-gray-500">Gross: {roundTotal || '--'}</span>
                                    <span className={`text-xs font-bold ${scoreColor(roundNetScore, scoringMethod)}`}>
                                      {formatScoreUtil(roundNetScore, scoringMethod)}
                                    </span>
                                  </div>
                                </div>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="border-b border-gray-100">
                                        <th className="px-1 py-1.5 text-gray-400 font-medium text-center w-8">Hole</th>
                                        {front9.map(h => (
                                          <th key={h.hole} className="px-1 py-1.5 text-gray-500 font-medium text-center">{h.hole}</th>
                                        ))}
                                        <th className="px-1.5 py-1.5 text-gray-700 font-bold text-center bg-gray-50">Out</th>
                                        {back9.map(h => (
                                          <th key={h.hole} className="px-1 py-1.5 text-gray-500 font-medium text-center">{h.hole}</th>
                                        ))}
                                        {back9.length > 0 && (
                                          <th className="px-1.5 py-1.5 text-gray-700 font-bold text-center bg-gray-50">In</th>
                                        )}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr>
                                        <td className="px-1 py-1.5 text-gray-400 font-medium text-center">Strokes</td>
                                        {front9.map(h => (
                                          <td key={h.hole} className="px-1 py-1.5 text-center font-semibold text-gray-800">{h.strokes}</td>
                                        ))}
                                        <td className="px-1.5 py-1.5 text-center font-bold text-gray-900 bg-gray-50">{front9Total || '--'}</td>
                                        {back9.map(h => (
                                          <td key={h.hole} className="px-1 py-1.5 text-center font-semibold text-gray-800">{h.strokes}</td>
                                        ))}
                                        {back9.length > 0 && (
                                          <td className="px-1.5 py-1.5 text-center font-bold text-gray-900 bg-gray-50">{back9Total || '--'}</td>
                                        )}
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })() : (
                      <p className="text-sm text-gray-400">No rounds assigned yet</p>
                    )}
                  </div>

                  {/* Pairings */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Pairings</h4>
                    {playerPairings.length > 0 ? (
                      <div className="space-y-3">
                        {playerPairings.map(pairing => (
                          <div key={pairing.round_number} className="border border-gray-100 rounded-xl overflow-hidden">
                            <div className="bg-gray-50 px-4 py-2.5 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-xs font-bold text-gray-700 uppercase">{roundNames[pairing.round_number] || `Round ${pairing.round_number}`}</span>
                                <span className="text-xs text-gray-500">Grp {pairing.group_number}</span>
                                {pairing.starting_hole && (
                                  <span className="text-xs text-emerald-600 font-medium">Hole {pairing.starting_hole}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-3">
                                {pairing.date && (
                                  <span className="text-xs text-gray-500">
                                    {new Date(pairing.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                  </span>
                                )}
                                {pairing.tee_time && (
                                  <span className="text-xs font-medium text-gray-600">{pairing.tee_time}</span>
                                )}
                              </div>
                            </div>
                            <div className="px-4 py-3">
                              {pairing.partners.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {pairing.partners.map((partner, i) => (
                                    <div key={i} className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg">
                                      <span className="text-xs font-medium text-gray-700">{partner.name}</span>
                                      {partner.handicap != null && (
                                        <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">HCP {partner.handicap}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-gray-400">No partners assigned</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400">No pairings available</p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}